import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { useParagraphInteractionsQuery, usePostParagraphInteraction, type ParagraphCommentDto } from '../hooks/useParagraphInteractions';
import { cn } from '@/utils/cn';
import { useAuth } from '@/features/authentication/contexts/AuthContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sectionId: number;
  paragraphIndex: number;
  theme?: 'white' | 'sepia' | 'dark';
  maxHeightVh?: number;
  bottomOffset?: string;
}

const CommentsBottomSheet: React.FC<Props> = ({
  isOpen,
  onClose,
  sectionId,
  paragraphIndex,
  theme = 'white',
  maxHeightVh = 80,
  bottomOffset,
}) => {
  const { data, isLoading, error } = useParagraphInteractionsQuery(isOpen ? sectionId : undefined, paragraphIndex);
  const { mutate: postInteraction, isPending } = usePostParagraphInteraction();
  const { user } = useAuth();
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  const [commentText, setCommentText] = useState('');
  const [replyTo, setReplyTo] = useState<{ id: number; name: string; content?: string } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const bgClass = theme === 'dark' ? 'bg-[#0f111a]' : theme === 'sepia' ? 'bg-[#d7c0a3]' : 'bg-[#f0f2f5]';
  const borderClass = theme === 'dark' ? 'border-gray-700' : theme === 'sepia' ? 'border-[#8f6c45]' : 'border-gray-200';
  const inputBgClass = theme === 'dark'
    ? 'bg-gray-800 border border-gray-700 text-white placeholder:text-gray-500'
    : theme === 'sepia'
      ? 'bg-[#e5dcc3] border border-[#d4c4a8] text-[#2d2416] placeholder:text-[#8a7a5a]'
      : 'bg-gray-100 border border-transparent text-gray-900 placeholder:text-gray-400 focus:bg-white';
  const inputPlaceholderClass = '';
  const allMessages = useMemo(() => {
    if (!data?.comments) return [];
    const flattened: (ParagraphCommentDto & { parentName?: string; parentContent?: string })[] = [];
    const process = (c: ParagraphCommentDto, parentName?: string, parentContent?: string) => {
      flattened.push({ ...c, parentName, parentContent });
      if (c.replies) c.replies.forEach((r) => process(r, c.user_name, c.content));
    };
    data.comments.forEach((c) => process(c));
    return flattened.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }, [data?.comments]);

  React.useEffect(() => {
    if (allMessages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allMessages.length]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const textToSubmit = commentText.trim();
    if (!textToSubmit || isPending) return;

    setSubmitError(null);
    setCommentText('');
    const currentReplyTo = replyTo;
    setReplyTo(null);
    if (textareaRef.current) textareaRef.current.style.height = '36px';

    postInteraction(
      {
        section_id: sectionId,
        paragraph_index: paragraphIndex,
        interaction_type: 'comment',
        content: textToSubmit,
        parent_interaction_id: currentReplyTo ? currentReplyTo.id : null,
        userProfile: user ? {
          name: (user as any).name || (user as any).username || 'Me',
          avatar_url: (user as any).avatar_url || ''
        } : undefined
      },
      {
        onError: (err: any) => {
          setCommentText(textToSubmit);
          setReplyTo(currentReplyTo);
          const detail = err?.response?.data?.detail;
          setSubmitError(detail ? (typeof detail === 'string' ? detail : JSON.stringify(detail)) : 'خطا در ثبت گفت‌وگو.');
        }
      }
    );
  };

  const renderMessage = (msg: ParagraphCommentDto & { parentName?: string; parentContent?: string }) => {
    const isMe = user?.id === msg.user_id.toString();
    const bubbleMine = theme === 'dark' ? 'bg-[#ffffff22]' : theme === 'sepia' ? 'bg-[#785533]' : 'bg-primary-500';
    const bubbleOther = theme === 'dark' ? 'bg-[#2b3048]' : theme === 'sepia' ? 'bg-[#8e6540]' : 'bg-[#6e61ff]';

    return (
      <div key={msg.interaction_id} className={cn('flex mb-5 w-full gap-2', isMe ? 'flex-row' : 'flex-row-reverse items-end')}>
        {!isMe && (
          <img
            src={msg.user_avatar_url}
            alt=""
            className="w-9 h-9 rounded-full bg-gray-300 flex-shrink-0 shadow-sm border border-black/5"
          />
        )}

        <div className={cn('max-w-[80%] flex flex-col relative min-w-[60px]', isMe ? 'items-start' : 'items-end')}>
          <div className={cn('relative px-3.5 py-2.5 rounded-2xl shadow-sm text-[13px] font-persian leading-relaxed break-words w-full text-white', isMe ? cn('rounded-br-none', bubbleMine) : cn('rounded-bl-none', bubbleOther))}>
            <div
              className={cn(
                'absolute bottom-0 w-3 h-3',
                isMe ? cn('-right-[5px] rounded-bl-full', bubbleMine) : cn('-left-[5px] rounded-br-full', bubbleOther)
              )}
              style={{ clipPath: isMe ? 'polygon(0 0, 100% 100%, 0 100%)' : 'polygon(100% 0, 100% 100%, 0 100%)' }}
            />

            {!isMe && <div className="text-[11px] font-extrabold mb-1.5 text-left text-white/95 tracking-wide">{msg.user_name}</div>}

            {msg.parentName && (
              <div className={cn('mb-2 border-r-2 pr-2 py-0.5 rounded-sm text-[10px] opacity-90 min-w-[80px]', isMe ? 'border-white/40 bg-white/10' : 'border-white/30 bg-white/10')}>
                <div className="font-bold truncate text-white">{msg.parentName}</div>
                <div className="truncate opacity-70 italic max-w-[120px]">{msg.parentContent}</div>
              </div>
            )}

            <div className="whitespace-pre-wrap leading-[1.9] relative z-10 text-[14px] font-medium">{msg.content}</div>
            <div className="text-[10px] mt-1.5 text-left text-white/85 font-semibold">
              {new Date(msg.created_at).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}
            </div>

            <button
              onClick={() => setReplyTo({ id: msg.interaction_id, name: msg.user_name, content: msg.content })}
              className="absolute inset-0 z-20 w-full h-full opacity-0 cursor-pointer"
              aria-label="Reply"
            />
          </div>
        </div>
        {isMe && <div className="w-1 flex-shrink-0" />}
      </div>
    );
  };

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <div className="fixed inset-0 z-[70] flex justify-center items-end" dir="rtl">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={cn('w-full max-w-lg rounded-t-3xl shadow-2xl flex flex-col relative z-[71] overflow-hidden', bgClass)}
            style={{ 
              height: bottomOffset
                ? `min(${maxHeightVh}svh, calc(100dvh - var(--onboarding-header-h, 110px) - 0.5rem))`
                : `${maxHeightVh}svh`,
              maxHeight: bottomOffset
                ? `min(${maxHeightVh}svh, calc(100dvh - var(--onboarding-header-h, 110px) - 0.5rem))`
                : `${maxHeightVh}svh`,
              paddingBottom: 'env(safe-area-inset-bottom)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn('flex items-center justify-center p-3 border-b relative', borderClass)}>
              <button onClick={onClose} className="absolute left-2 p-2 rounded-full hover:bg-black/5 opacity-70 hover:opacity-100 transition-all">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h3 className={cn('font-persian font-bold text-base text-center', theme === 'dark' || theme === 'sepia' ? 'text-white' : 'text-gray-800')}>
                گفت و گو ({allMessages.length})
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 hide-scrollbar">
              {isLoading && (
                <div className="flex justify-center items-center h-full opacity-60">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                </div>
              )}
              {error && <div className="text-center text-red-500 text-sm mt-10 font-persian">خطا در دریافت گفت‌وگو...</div>}

              {!isLoading && !error && data?.is_premium === false && (
                <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
                  <span className="text-5xl">🔒</span>
                  <p className={cn('font-persian font-bold text-sm leading-relaxed', theme === 'dark' ? 'text-gray-200' : 'text-gray-700')}>
                    برای دیدن یا ارسال گفت‌وگو، اشتراک بوکا نیاز است.
                  </p>
                </div>
              )}

              {!isLoading && !error && data?.is_premium !== false && allMessages.length === 0 && (
                <div className="text-center opacity-50 text-sm mt-10 font-persian flex flex-col items-center gap-3">
                  <span className="text-4xl text-primary-500/50">💬</span>
                  <p>هنوز گفت‌وگویی ثبت نشده. شروع‌کننده باشید!</p>
                </div>
              )}

              {!isLoading && data?.is_premium !== false && allMessages.map((m) => renderMessage(m))}
              <div ref={messagesEndRef} />
            </div>

            <div className={cn('px-4 pt-2 pb-1 border-t', borderClass)}>
              {submitError && (
                <div className="mb-2 px-3 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg text-[10px] text-red-500 font-persian flex items-center justify-between">
                  <span>{submitError}</span>
                  <button onClick={() => setSubmitError(null)} className="opacity-60 hover:opacity-100">×</button>
                </div>
              )}

              {replyTo && (
                <div className={cn('flex justify-between items-center mb-1.5 px-3 py-1 rounded-lg border-r-2', theme === 'sepia' ? 'bg-[#e5dcc3] border-[#8B5A2B]' : 'bg-primary-500/5 border-primary-500')}>
                  <div className="flex flex-col min-w-0">
                    <span className={cn('text-[10px] font-persian font-bold', theme === 'sepia' ? 'text-[#5c4a2f]' : 'text-primary-500')}>در حال پاسخ به: {replyTo.name}</span>
                    <span className={cn('text-[10px] font-persian truncate', theme === 'sepia' ? 'text-[#6e5a3e]' : theme === 'dark' ? 'text-white/85' : 'opacity-60')}>{replyTo.content}</span>
                  </div>
                  <button onClick={() => setReplyTo(null)} className="text-xs opacity-60 hover:opacity-100 p-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className="flex flex-row-reverse gap-2 items-center"
                // In onboarding the Continue CTA is fixed to the viewport bottom.
                // Keep the comment controls above it with a small, consistent gap.
                style={{ marginBottom: bottomOffset ? '5.5rem' : undefined }}
              >
                <input
                  ref={textareaRef as any}
                  placeholder="درباره این پاراگراف چیزی بگو !"
                  value={commentText}
                  onChange={(e) => {
                    setCommentText(e.target.value);
                    setSubmitError(null);
                  }}
                  className={cn(
                    'm-0 flex-1 h-10 self-center px-4 rounded-2xl text-sm font-persian leading-none focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200',
                    inputBgClass,
                    inputPlaceholderClass
                  )}
                  style={{ lineHeight: '40px' }}
                  disabled={isPending}
                  dir="rtl"
                />

                <button
                  type="submit"
                  disabled={!commentText.trim() || isPending}
                  className={cn(
                    'm-0 flex items-center justify-center w-10 h-10 self-center rounded-full shadow-sm flex-shrink-0 text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-transform active:scale-95',
                    !commentText.trim() || isPending
                      ? 'bg-gray-300 text-gray-500'
                      : theme === 'dark'
                        ? 'bg-purple-600 hover:bg-purple-700'
                        : theme === 'sepia'
                          ? 'bg-[#8B5A2B] hover:bg-[#6B4423]'
                          : 'bg-primary-500 hover:bg-primary-600'
                  )}
                >
                  {isPending ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <ArrowUp className="w-5 h-5" />
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CommentsBottomSheet;
